/* eslint-disable no-console */
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Col,
  DetailList,
  Heading,
  Row,
  TipBox,
} from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Product } from '../../store/store.types'
import { prepareConfusionMatrixData } from '../../utils/charts'
import HtmlModal from '../HtmlModal/HtmlModal'
import {
  ChartConfusionMatrix,
  PredictedExpectedSelection,
} from './ChartConfusionMatrix'
import useStyle from './ChartConfusionMatrix.styles'

export interface ConfusionMatrixInputData {
  matrix: number[][]
  classLabels: string[]
}

interface ConfusionMatrixProps {
  data: ConfusionMatrixInputData
  withDetails?: boolean
  globalResultTitle: string
  globalResultValue: string
  samplesTypeTitle: string
  product?: Product
  falseBadRate?: string
  falseGoodRate?: string
  onPredictedExpectedClicked?: (selection: PredictedExpectedSelection) => void
  prediction?: string
  isFineTuning?: boolean
}

const ConfusionMatrix = ({
  data,
  withDetails = false,
  globalResultTitle,
  globalResultValue,
  samplesTypeTitle,
  product,
  falseBadRate,
  falseGoodRate,
  onPredictedExpectedClicked,
  prediction,
  isFineTuning,
}: ConfusionMatrixProps) => {
  const { t } = useTranslation()
  const chartData = prepareConfusionMatrixData(data)
  const classes = useStyle()
  const [htmlHelperModal, setHtmlHelperModal] = useState<boolean>(false)

  return (
    <>
      {withDetails ? (
        <Row>
          <Col xs={12}>
            <Heading headingLevel="3" headingLevelStyle="4" noBottomMargin>
              {t('Performance details')}
            </Heading>
            <DetailList
              titleColumnWidth="6rem"
              list={[
                {
                  title: globalResultTitle,
                  value: (
                    <>
                      {globalResultValue}
                      {product && (
                        <FontAwesomeIcon
                          icon={faCircleQuestion}
                          onClick={() => {
                            setHtmlHelperModal(true)
                          }}
                        />
                      )}
                    </>
                  ),
                },
                {
                  title: samplesTypeTitle,
                  value: data.matrix.flat().reduce((a, b) => a + b, 0),
                },
                {
                  title: t('Matched'),
                  value: chartData.data.reduce(
                    (sum: number, d) => (d.x === d.y ? sum + d.value : sum),
                    0
                  ),
                },
                {
                  title: t('Not matched'),
                  value: chartData.data.reduce(
                    (sum: number, d) => (d.x !== d.y ? sum + d.value : sum),
                    0
                  ),
                },
                ...(product && product.productType.name === 'anomaly'
                  ? [
                      {
                        title: t('False bad rate'),
                        value: falseBadRate,
                      },
                      {
                        title: t('False good rate'),
                        value: falseGoodRate,
                      },
                    ]
                  : []),
              ]}
            />
            <p className={classes.chartHeader}>
              {`${t('Confusion matrix')}${prediction ? ' ⬤' : ''}`}
            </p>
            <ChartConfusionMatrix
              data={chartData}
              onPredictedExpectedClicked={onPredictedExpectedClicked}
            />
          </Col>
        </Row>
      ) : (
        <ChartConfusionMatrix
          data={chartData}
          onPredictedExpectedClicked={onPredictedExpectedClicked}
        />
      )}
      {isFineTuning && (
        <TipBox>
          {t(
            'Trainings conducted with the Fine Tuning backbone require a substantial amount of time.'
          )}
          <br />
          {t(
            'Consequently, the trainings performance results are derived by allocating 30% of the training data for evaluation, deviating from the k-folds validation method typically employed in AI-go Studio and described in the user manual.'
          )}
          <br />
          {t(
            'For this reason, the accuracy displayed on the screen may not be indicative of the actual capabilities of the model and it is necessary to run a test on a new data set to obtain a more accurate evaluation.'
          )}
        </TipBox>
      )}
      {htmlHelperModal && product && (
        <HtmlModal
          productType={product.type}
          onCloseRequest={() => setHtmlHelperModal(false)}
        />
      )}
    </>
  )
}

export default ConfusionMatrix
